import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "sonner";
import { socketConnection } from "../../services/socket";
import n8n from "../../assets/n8n.png";
import dialogflow from "../../assets/dialogflow.png";
import webhooks from "../../assets/webhook.png"
import typebot from "../../assets/typebot.jpg";

import { makeStyles } from "@material-ui/core/styles";

import {
  Avatar,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
} from "@material-ui/core";

import {
  DeleteOutline,
  Edit,
  Add as AddIcon
} from "@material-ui/icons";

import SearchIcon from "@material-ui/icons/Search";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import IntegrationModal from "../../components/QueueIntegrationModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import useTableStyles from "../../components/TableStyles";
import Skeleton from "@material-ui/lab/Skeleton";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useDate } from "../../hooks/useDate";

const reducer = (state, action) => {
  if (action.type === "LOAD_INTEGRATIONS") {
    const queueIntegration = action.payload;
    const newIntegrations = [];

    queueIntegration.forEach((integration) => {
      const integrationIndex = state.findIndex((u) => u.id === integration.id);
      if (integrationIndex !== -1) {
        state[integrationIndex] = integration;
      } else {
        newIntegrations.push(integration);
      }
    });

    return [...state, ...newIntegrations];
  }

  if (action.type === "UPDATE_INTEGRATIONS") {
    const queueIntegration = action.payload;
    const integrationIndex = state.findIndex((u) => u.id === queueIntegration.id);

    if (integrationIndex !== -1) {
      state[integrationIndex] = queueIntegration;
      return [...state];
    } else {
      return [queueIntegration, ...state];
    }
  }

  if (action.type === "DELETE_INTEGRATION") {
    const integrationId = action.payload;

    const integrationIndex = state.findIndex((u) => u.id === integrationId);
    if (integrationIndex !== -1) {
      state.splice(integrationIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2, 3),
    borderRadius: 14,
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.06)"
      : "1px solid #E2E8F0",
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
  },
  integrationsGrid: {
    marginTop: theme.spacing(2),
  },
  integrationCard: {
    borderRadius: 12,
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.06)"
      : "1px solid #E2E8F0",
    boxShadow: "none",
    backgroundColor: theme.palette.background.paper,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    transition: "all 0.2s ease",
    "&:hover": {
      boxShadow: theme.shadows[2],
      transform: "translateY(-2px)",
    },
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    paddingBottom: theme.spacing(1.5),
    borderBottom: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.04)"
      : "1px solid #F1F5F9",
    marginBottom: theme.spacing(1.5),
  },
  cardAvatar: {
    width: 180,
    height: 60,
    borderRadius: 8,
    objectFit: "contain",
  },
  cardBody: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  cardName: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: 16,
    fontWeight: 600,
    color: theme.palette.type === "dark" ? "#F1F5F9" : "#1E293B",
    marginBottom: theme.spacing(0.5),
  },
  cardId: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: 13,
    color: theme.palette.type === "dark" ? "#94A3B8" : "#64748B",
  },
  cardCreatedAt: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: 12,
    color: theme.palette.type === "dark" ? "#64748B" : "#94A3B8",
    marginTop: theme.spacing(0.5),
  },
  cardFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    marginTop: "auto",
    paddingTop: theme.spacing(1.5),
    borderTop: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.04)"
      : "1px solid #F1F5F9",
  },
  searchField: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      fontFamily: '"Inter", "Poppins", sans-serif',
      "& fieldset": {
        borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.12)" : "#E2E8F0",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.2)" : "#CBD5E1",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#35af9a",
      },
    },
  },
  addButton: {
    borderRadius: 10,
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontWeight: 600,
    fontSize: 14,
    padding: "8px 16px",
    color: theme.palette.type === "dark" ? "#E2E8F0" : "#1E293B",
    backgroundColor: "transparent",
    boxShadow: "none",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.12)"
      : "1px solid #E2E8F0",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark"
        ? "rgba(255,255,255,0.04)"
        : "#F8FAFC",
      boxShadow: "none",
    },
  },
  skeletonCard: {
    borderRadius: 12,
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255,255,255,0.06)"
      : "1px solid #E2E8F0",
    boxShadow: "none",
    height: 180,
  },
  editBtn: {
    "&:hover": {
      backgroundColor: "rgba(59, 130, 246, 0.08)",
      color: "#3B82F6",
    },
  },
  deleteBtn: {
    "&:hover": {
      backgroundColor: "rgba(239, 68, 68, 0.08)",
      color: "#EF4444",
    },
  },
}));

const QueueIntegration = () => {
  const classes = useStyles();
  const tableClasses = useTableStyles();
  const { dateToClient } = useDate();

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [queueIntegration, dispatch] = useReducer(reducer, []);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchIntegrations = async () => {
        try {
          const { data } = await api.get("/queueIntegration/", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_INTEGRATIONS", payload: data.queueIntegrations });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchIntegrations();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;
    const socket = socketConnection({ companyId, userId: user.id });

    socket.on(`company-${companyId}-queueIntegration`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_INTEGRATIONS", payload: data.queueIntegration });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_INTEGRATION", payload: +data.integrationId });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleOpenUserModal = () => {
    setSelectedIntegration(null);
    setUserModalOpen(true);
  };

  const handleCloseIntegrationModal = () => {
    setSelectedIntegration(null);
    setUserModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditIntegration = (queueIntegration) => {
    setSelectedIntegration(queueIntegration);
    setUserModalOpen(true);
  };

  const handleDeleteIntegration = async (integrationId) => {
    try {
      await api.delete(`/queueIntegration/${integrationId}`);
      toast.success(i18n.t("queueIntegration.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingUser(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const getIntegrationLogo = (type) => {
    switch (type) {
      case "dialogflow":
        return dialogflow;
      case "n8n":
        return n8n;
      case "webhook":
        return webhooks;
      case "typebot":
        return typebot;
      default:
        return null;
    }
  };

  const getIntegrationTypeLabel = (type) => {
    switch (type) {
      case "dialogflow":
        return "Dialogflow";
      case "n8n":
        return "n8n";
      case "webhook":
        return "Webhook";
      case "typebot":
        return "Typebot";
      default:
        return type;
    }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingUser &&
          `${i18n.t("queueIntegration.confirmationModal.deleteTitle")} ${deletingUser.name
          }?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteIntegration(deletingUser.id)}
      >
        {i18n.t("queueIntegration.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <IntegrationModal
        open={userModalOpen}
        onClose={handleCloseIntegrationModal}
        aria-labelledby="form-dialog-title"
        integrationId={selectedIntegration && selectedIntegration.id}
      />
      <MainHeader>
        <Title>{i18n.t("queueIntegration.title")} ({queueIntegration.length})</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("queueIntegration.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            variant="outlined"
            size="small"
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="secondary" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            onClick={handleOpenUserModal}
            className={classes.addButton}
            startIcon={<AddIcon />}
          >
            Integração
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        onScroll={handleScroll}
      >
        <Grid container spacing={2} className={classes.integrationsGrid}>
          {queueIntegration.map((integration) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={integration.id}>
              <Card className={classes.integrationCard}>
                <CardContent style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                  <Box className={classes.cardHeader}>
                    {getIntegrationLogo(integration.type) && (
                      <Avatar
                        src={getIntegrationLogo(integration.type)}
                        className={classes.cardAvatar}
                        variant="square"
                      />
                    )}
                  </Box>
                  <Box className={classes.cardBody}>
                    <Typography className={classes.cardName}>
                      {integration.name}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Typography className={classes.cardId}>
                        ID: {integration.id}
                      </Typography>
                      <Chip
                        label={getIntegrationTypeLabel(integration.type)}
                        size="small"
                        style={{
                          fontSize: 11,
                          height: 20,
                          fontWeight: 600,
                          backgroundColor: "rgba(53, 175, 154, 0.1)",
                          color: "#35af9a",
                        }}
                      />
                    </Box>
                    {integration.createdAt && (
                      <Typography className={classes.cardCreatedAt}>
                        Criado em: {dateToClient(integration.createdAt)}
                      </Typography>
                    )}
                  </Box>
                  <Box className={classes.cardFooter}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditIntegration(integration)}
                      className={`${tableClasses.actionButton} ${classes.editBtn}`}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setConfirmModalOpen(true);
                        setDeletingUser(integration);
                      }}
                      className={`${tableClasses.actionButton} ${classes.deleteBtn}`}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
          {loading && (
            <>
              {[...Array(4)].map((_, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={`skeleton-${index}`}>
                  <Card className={classes.skeletonCard}>
                    <CardContent>
                      <Skeleton variant="rectangular" width="100%" height={40} style={{ marginBottom: 16, borderRadius: 8 }} />
                      <Skeleton variant="text" width="60%" height={24} style={{ marginBottom: 8 }} />
                      <Skeleton variant="text" width="40%" height={20} />
                      <Box display="flex" justifyContent="flex-end" gap={1} marginTop={2}>
                        <Skeleton variant="circular" width={32} height={32} />
                        <Skeleton variant="circular" width={32} height={32} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </>
          )}
        </Grid>
      </Paper>
    </MainContainer>
  );
};

export default QueueIntegration;